import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';

import Icon from '_components/atomic/Icon';

const { colors, devices, fontSizes, spaces } = theme;

const StyledStepperContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media ${devices.tablet} {
    display: none;
  }
`;

const StyledStepContainer = styled.div`
  display: flex;

  ${({ reverse }) =>
    reverse &&
    css`
      flex-direction: row-reverse;
    `}
`;

const StyledImageContainer = styled.div`
  min-width: 370px;
  margin-bottom: ${spaces.large15X}px;
`;

const StyledStepLink = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${spaces.large2X}px;
  margin-right: ${spaces.large2X}px;
`;

const StyledBullet = styled.div``;

const StyledLine = styled.div`
  flex-grow: 1;
  position: relative;

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    border-right-width: 1px;
    border-right-style: ${({ active }) => (active ? 'solid' : 'dashed')};
    border-right-color: ${({ active }) =>
      active ? colors.indigo[600] : colors.gray[300]};
  }
`;

const StyledStepInfo = styled.div`
  flex-grow: 1;
  max-width: 370px;
  padding-top: ${spaces.small2X}px;
`;

const StyledTitle = styled.div`
  margin-bottom: ${spaces.medium}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.regular}px;
  line-height: 130%;
  text-align: ${({ right }) => (right ? 'right' : 'left')};
  color: ${({ active }) => (active ? colors.indigo[500] : colors.gray[600])};
`;

const StyledSubtitle = styled.div`
  margin-bottom: ${spaces.medium}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 500;
  font-size: ${fontSizes.large2X}px;
  line-height: 130%;
  text-align: ${({ right }) => (right ? 'right' : 'left')};
  color: ${({ active }) => (active ? colors.indigo[600] : colors.gray[800])};
`;

const StyledDescription = styled.div`
  margin-bottom: ${spaces.medium}px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.medium}px;
  text-align: ${({ right }) => (right ? 'right' : 'left')};
  color: ${colors.gray[700]};
`;

const InfoStepperDesktop = ({ activeStepIdx, steps = [] }) => (
  <StyledStepperContainer>
    {steps.map(
      ({ title, subtitle, description, imageSrc, imageAlt, reverse }, idx) => {
        const lastIdx = steps.length - 1;
        const active = idx === activeStepIdx;
        const iconAlias = active ? ICONS_ALIASES.stepDone : ICONS_ALIASES.step;
        const iconColor = active ? colors.indigo[600] : colors.gray[300];

        return (
          <StyledStepContainer key={`${title}:${idx}`} reverse={reverse}>
            <StyledImageContainer>
              <picture>
                <source srcSet={imageSrc} type="image/svg+xml" />
                <img src={imageSrc} title={imageAlt} alt={imageAlt} />
              </picture>
            </StyledImageContainer>
            <StyledStepLink>
              <StyledBullet>
                <Icon alias={iconAlias} color={iconColor} />
              </StyledBullet>
              <StyledLine active={active} hidden={idx === lastIdx} />
            </StyledStepLink>
            <StyledStepInfo>
              <StyledTitle active={active} right={reverse}>
                {title}
              </StyledTitle>
              <StyledSubtitle active={active} right={reverse}>
                {subtitle}
              </StyledSubtitle>
              <StyledDescription active={active} right={reverse}>
                {description}
              </StyledDescription>
            </StyledStepInfo>
          </StyledStepContainer>
        );
      }
    )}
  </StyledStepperContainer>
);

InfoStepperDesktop.propTypes = {
  activeStepIdx: PropTypes.number,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node,
      subtitle: PropTypes.node,
      description: PropTypes.node,
      imageSrc: PropTypes.string,
    })
  ),
};

export default InfoStepperDesktop;
